import { useState, useRef, useEffect, useCallback } from 'react';
import { Camera, X, MagnifyingGlass } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useT } from '@/i18n/LanguageContext';

const REGION = 'scanner-camera-region';

/** Continuous camera + manual barcode input. Calls onDetected(code) per unique scan. */
export default function ScannerScanner({ onDetected, disabled }) {
  const { t } = useT();
  const [code, setCode] = useState('');
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);
  const lastRef = useRef({ code: '', at: 0 });

  const stopScan = useCallback(async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      } catch (err) { console.debug('[scanner] stop failed', err); }
      scannerRef.current = null;
    }
    setScanning(false);
  }, []);

  const startScan = async () => {
    setScanning(true);
    try {
      const mod = await import('html5-qrcode');
      const inst = new mod.Html5Qrcode(REGION, {
        // Use the browser BarcodeDetector API when available — much faster
        // and supports wider formats (Code128, QR, DataMatrix, EAN/UPC, ITF).
        experimentalFeatures: { useBarCodeDetectorIfSupported: true },
      });
      scannerRef.current = inst;
      // Prefer wide format support if the enum is exposed by the lib.
      const F = mod.Html5QrcodeSupportedFormats;
      const formats = F ? [
        F.QR_CODE, F.CODE_128, F.CODE_39, F.EAN_13, F.EAN_8,
        F.UPC_A, F.UPC_E, F.ITF, F.DATA_MATRIX, F.PDF_417, F.CODABAR,
      ].filter(Boolean) : undefined;
      await inst.start(
        // Continuous autofocus + higher preferred resolution — resolves iOS/Android
        // "picture blurry when close to code" complaint. Falls back gracefully on
        // devices without focus-mode support.
        {
          facingMode: 'environment',
          advanced: [{ focusMode: 'continuous' }],
          focusMode: 'continuous',
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        {
          fps: 15,
          qrbox: { width: 280, height: 200 },
          aspectRatio: 1.7,
          disableFlip: false,
          ...(formats ? { formatsToSupport: formats } : {}),
        },
        (decoded) => {
          const now = Date.now();
          // Debounce duplicate reads of the same code within 1.5s (continuous mode).
          if (decoded === lastRef.current.code && now - lastRef.current.at < 1500) return;
          lastRef.current = { code: decoded, at: now };
          onDetected(decoded);
        },
        () => { /* per-frame errors ignored */ },
      );
    } catch (err) {
      console.warn('[scanner] start failed', err);
      setScanning(false);
    }
  };

  useEffect(() => () => { stopScan(); }, [stopScan]);

  const submitManual = () => {
    const v = code.trim();
    if (!v) return;
    onDetected(v);
    setCode('');
  };

  return (
    <div className="space-y-3" data-testid="scanner-scan-panel">
      <div id={REGION} className={`${scanning ? 'block' : 'hidden'} rounded-xl overflow-hidden bg-black aspect-[4/3]`} />
      {!scanning
        ? <Button variant="outline" onClick={startScan} disabled={disabled} data-testid="scanner-camera-start"
            className="w-full h-12 border-cyan-200 text-cyan-700 hover:bg-cyan-50 text-base"><Camera size={20} className="mr-2" weight="bold" /> {t('scanner.scan.start')}</Button>
        : <Button variant="outline" onClick={stopScan} data-testid="scanner-camera-stop"
            className="w-full h-12 text-base"><X size={20} className="mr-2" /> {t('scanner.scan.stop')}</Button>}
      <div className="flex gap-2">
        <Input value={code} onChange={(e) => setCode(e.target.value)} disabled={disabled}
          onKeyDown={(e) => e.key === 'Enter' && submitManual()}
          placeholder={t('scanner.scan.manual')} className="h-12 font-mono text-base" data-testid="scanner-manual-input" />
        <Button onClick={submitManual} disabled={disabled} data-testid="scanner-manual-btn"
          className="h-12 px-4 bg-teal-700 hover:bg-teal-800"><MagnifyingGlass size={18} /></Button>
      </div>
      <p className="text-[11px] text-zinc-400 text-center">{t('scanner.scan.hint')}</p>
    </div>
  );
}
